// src/pages/CapturePage/rawRecorder.js
//
// Unprocessed recording, for the two blocks that are measured rather than
// recognised.
//
// The practice flow records with echo cancellation and noise suppression on and
// encodes to Opus. That is right for speech recognition — Azure transcribes
// better — and wrong for acoustic measurement: those filters remove exactly the
// micro-variations that jitter, shimmer and harmonics-to-noise ratio are defined
// as measuring. A rate or a voice figure taken from processed Opus would look
// plausible and describe the microphone.
//
// So the Fast Talking Game and the Long Sound Game record through this instead:
// every constraint off, and 16-bit PCM WAV written here, because MediaRecorder
// cannot emit WAV in any browser we target.

import { RAW_AUDIO_CONSTRAINTS } from './noiseMeter';

// The worklet is a string rather than a file: Create React App will not emit a
// separate worklet bundle, and addModule needs a URL of its own.
//
// It batches about 85 ms of audio per message instead of posting every 128-sample
// quantum. Posting each quantum meant 375 messages a second, every one of them
// waking the main thread while React was animating a countdown — the main thread
// fell behind, and whatever was still queued when the context closed was lost.
// Real recordings came back more than a second short of their eight.
//
// On 'flush' it emits whatever it still holds and then answers 'done'. Because a
// MessagePort delivers in order, 'done' arriving is proof that every earlier
// frame has already been handed over. That is what makes tearing down safe.
const WORKLET_SOURCE = `
const BATCH_SAMPLES = 4096;

class RawTap extends AudioWorkletProcessor {
  constructor() {
    super();
    this.held = [];
    this.heldLength = 0;
    this.finished = false;
    this.port.onmessage = (event) => {
      if (event.data !== 'flush') return;
      this.emit();
      this.port.postMessage('done');
      this.finished = true;
    };
  }

  emit() {
    if (!this.heldLength) return;
    const out = new Float32Array(this.heldLength);
    let at = 0;
    for (const chunk of this.held) { out.set(chunk, at); at += chunk.length; }
    this.held = [];
    this.heldLength = 0;
    // Transfer rather than copy: the buffer is handed over, not cloned.
    this.port.postMessage(out, [out.buffer]);
  }

  process(inputs) {
    if (this.finished) return false;
    const channel = inputs[0] && inputs[0][0];
    if (channel && channel.length) {
      this.held.push(new Float32Array(channel));
      this.heldLength += channel.length;
      if (this.heldLength >= BATCH_SAMPLES) this.emit();
    }
    return true;
  }
}
registerProcessor('raw-tap', RawTap);
`;

// How often the caller hears about the level. The meter only has to look alive;
// a React state update per audio frame is what caused the dropped audio.
const LEVEL_INTERVAL_MS = 100;

// A flush that never answers must not hang the session. One batch is ~85 ms, so
// half a second is generous.
const FLUSH_TIMEOUT_MS = 500;

export function encodeWav(samples, sampleRate) {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);
    const text = (offset, s) => {
        for (let i = 0; i < s.length; i += 1) view.setUint8(offset + i, s.charCodeAt(i));
    };

    text(0, 'RIFF');
    view.setUint32(4, 36 + samples.length * 2, true);
    text(8, 'WAVE');
    text(12, 'fmt ');
    view.setUint32(16, 16, true);       // PCM header size
    view.setUint16(20, 1, true);        // PCM, uncompressed
    view.setUint16(22, 1, true);        // mono
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);       // bits per sample
    text(36, 'data');
    view.setUint32(40, samples.length * 2, true);

    let offset = 44;
    for (let i = 0; i < samples.length; i += 1) {
        // Clip rather than wrap: a wrapped sample is a loud click that reads as
        // a voice break.
        const s = Math.max(-1, Math.min(1, samples[i]));
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
        offset += 2;
    }
    return new Blob([view], { type: 'audio/wav' });
}

export function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

/**
 * Start an unprocessed recording. Returns a handle with stop() -> { blob,
 * base64, durationMs, sampleRate } and cancel().
 *
 * onLevel(peak 0..1) fires as audio arrives, so a game can animate to the
 * child's own voice.
 */
export async function startRawRecording({ onLevel } = {}) {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: RAW_AUDIO_CONSTRAINTS });

    // Trust the track, not the request: a device may refuse to switch a filter off.
    const settings = (stream.getAudioTracks()[0] || {}).getSettings
        ? stream.getAudioTracks()[0].getSettings()
        : {};

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) {
        stream.getTracks().forEach((t) => t.stop());
        throw new Error('This browser cannot record uncompressed audio.');
    }
    const context = new AudioCtx();
    // Read now, not at stop(): a closed context reports no sample rate in some
    // browsers, and this number is the denominator of every measurement.
    const sampleRate = context.sampleRate || 48000;
    const source = context.createMediaStreamSource(stream);
    const chunks = [];
    const startedAt = Date.now();
    let node = null;
    let usingWorklet = false;
    let drained = null;          // resolves when the worklet says 'done'
    let lastLevelAt = 0;

    const report = (frame) => {
        if (!onLevel) return;
        const now = Date.now();
        if (now - lastLevelAt < LEVEL_INTERVAL_MS) return;
        lastLevelAt = now;
        let peak = 0;
        for (let i = 0; i < frame.length; i += 1) {
            const v = Math.abs(frame[i]);
            if (v > peak) peak = v;
        }
        onLevel(peak);
    };

    const collect = (frame) => {
        chunks.push(frame);
        report(frame);
    };

    if (context.audioWorklet) {
        // Inside the try: if object URLs are unavailable the recorder falls back
        // to the ScriptProcessor rather than failing the whole recording.
        let url = null;
        try {
            url = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'application/javascript' }));
            await context.audioWorklet.addModule(url);
            node = new window.AudioWorkletNode(context, 'raw-tap');
            usingWorklet = true;
            let settle = null;
            drained = new Promise((resolve) => { settle = resolve; });
            node.port.onmessage = (e) => {
                if (e.data === 'done') settle();
                else collect(e.data);
            };
        } catch (e) {
            // A browser that cannot load the module falls through to the
            // ScriptProcessor path rather than losing the recording.
            node = null;
            usingWorklet = false;
        } finally {
            if (url) URL.revokeObjectURL(url);
        }
    }
    if (!node) {
        // Older Safari and jsdom: deprecated, but it is this or no raw path.
        node = context.createScriptProcessor(4096, 1, 1);
        node.onaudioprocess = (e) => collect(new Float32Array(e.inputBuffer.getChannelData(0)));
    }

    source.connect(node);
    // A worklet with nothing downstream is not pulled in some browsers; a muted
    // gain keeps it running without the child hearing themselves.
    const silence = context.createGain();
    silence.gain.value = 0;
    node.connect(silence);
    silence.connect(context.destination);

    const teardown = () => {
        try { source.disconnect(); node.disconnect(); silence.disconnect(); } catch (e) { /* already gone */ }
        stream.getTracks().forEach((t) => t.stop());
        if (context.state !== 'closed') context.close();
    };

    /**
     * Wait until every frame the worklet produced has reached us.
     *
     * Tearing down first was the bug: closing the context threw away whatever
     * was still in flight, and recordings came back up to 1.2 seconds short of
     * their eight. A short recording is not a harmless one — the duration is the
     * denominator of the syllable rate, so the missing tail moves the number that
     * a therapist reads as "slow".
     */
    const drain = async () => {
        if (!usingWorklet) {
            // The ScriptProcessor path runs on this thread, so there is no queue
            // to drain — only the buffer currently filling. One buffer is ~85 ms.
            await new Promise((resolve) => setTimeout(resolve, 120));
            return;
        }
        node.port.postMessage('flush');
        await Promise.race([
            drained,
            new Promise((resolve) => setTimeout(resolve, FLUSH_TIMEOUT_MS)),
        ]);
    };

    return {
        constraintsUsed: RAW_AUDIO_CONSTRAINTS,
        trackSettings: settings,
        sampleRate,
        cancel: teardown,
        async stop() {
            await drain();
            teardown();

            const total = chunks.reduce((n, c) => n + c.length, 0);
            const samples = new Float32Array(total);
            let at = 0;
            chunks.forEach((c) => { samples.set(c, at); at += c.length; });

            const blob = encodeWav(samples, sampleRate);
            return {
                blob,
                base64: await blobToBase64(blob),
                durationMs: total ? Math.round((total / sampleRate) * 1000) : Date.now() - startedAt,
                sampleRate,
                // What the clock says, so a caller can see the two disagree when
                // frames have gone missing.
                elapsedMs: Date.now() - startedAt,
            };
        },
    };
}
